function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect } from 'react';
import { throttle } from '../../utils/index.js';
const desktopBreakpoint = 992;

/**
 * Check if we're on a desktop device.
 *
 * This isn't using the custom hook as it's problematic with the setup of this
 * HOC. This whole accessible menu thing is a little complex and due a refactor
 * anyway.
 */
const isDesktop = () => typeof window !== 'undefined' ? window.innerWidth >= desktopBreakpoint : true;

/**
 * Check if a menu item has children.
 */
const hasChildren = item => Array.isArray(item.children) && item.children.length > 0;

/**
 * Get the keys of item and all of its descendants.
 */
const getDescendantKeys = menuItem => [].concat(menuItem.key, ...(menuItem.children || []).map(getDescendantKeys));

/**
 * Find a menu item from its DOM node.
 */
const findMenuItem = (menuItems, domNode) => {
  let foundItem = null;
  menuItems.forEach(currentItem => {
    if (currentItem.domNode === domNode) {
      foundItem = currentItem;
    }
    if (currentItem.hasChildren) {
      const child = findMenuItem(currentItem.children, domNode);
      if (child) {
        foundItem = child;
      }
    }
    return null;
  });
  return foundItem;
};

/**
 * Handle the enter key being pressed.
 */
const handleEnter = (evt, item) => {
  if (item.hasChildren && item.level === 'menubar') {
    evt.preventDefault();
    item.children[0].activate(item);
  } else {
    item.domNode.click();
  }
};

/**
 * Handle the down arrow key being pressed.
 */
const handleDownArrow = (evt, item) => {
  evt.preventDefault();
  if (!isDesktop()) {
    if (item.next()) {
      item.next().activate(item);
    }
  } else if (item.hasChildren) {
    item.children[0].activate(item);
  } else if (item.next()) {
    item.next().activate(item);
  }
};

/**
 * Handle the up arrow key being pressed.
 */
const handleUpArrow = (evt, item) => {
  evt.preventDefault();
  if (!isDesktop()) {
    if (item.prev()) {
      item.prev().activate(item);
    }
  } else if (item.prev()) {
    item.prev().activate(item);
  } else if (item.parent) {
    item.parent.activate(item);
  }
};

/**
 * Handle the left arrow key being pressed.
 */
const handleLeftArrow = (evt, item) => {
  evt.preventDefault();
  if (!isDesktop()) {
    if (item.parent) {
      item.parent.activate(item);
    }
  } else if (item.parent && item.parent.level === 'menu') {
    item.parent.activate(item);
  } else if (item.prev()) {
    item.prev().activate(item, item.prev().level !== 'menubar');
  } else if (item.parent) {
    item.parent.activate(item);
  }
};

/**
 * Handle the right arrow key being pressed.
 */
const handleRightArrow = (evt, item) => {
  evt.preventDefault();
  if (!isDesktop()) {
    if (item.hasChildren) {
      item.children[0].activate(item);
    }
  } else if (item.parent && item.parent.level === 'menu' && item.parent.next()) {
    item.parent.next().activate(item);
  } else if (item.next()) {
    item.next().activate(item, item.next().level !== 'menubar');
  } else if (item.hasChildren) {
    item.children[0].activate(item);
  }
};

/**
 * Handle the home key being pressed.
 */
const handleHome = (evt, item) => {
  evt.preventDefault();
  let firstItem = item;
  while (firstItem.prev()) {
    firstItem = firstItem.prev();
  }
  firstItem.activate(item);
};

/**
 * Handle the end key being pressed.
 */
const handleEnd = (evt, item) => {
  evt.preventDefault();
  let lastItem = item;
  while (lastItem.next() && !lastItem.next().data.mobile) {
    lastItem = lastItem.next();
  }
  lastItem.activate(item);
};

/**
 * Handle the escape key being pressed.
 */
const handleEscape = (evt, item, menu) => {
  evt.preventDefault();
  let lastItem = item;
  while (lastItem.parent) {
    lastItem = lastItem.parent;
  }
  lastItem.activate(item);
  menu.clear();
};

/**
 * Handle the tab key being pressed.
 */
const handleTab = (item, menu) => {
  let lastItem = item;
  while (lastItem.parent) {
    lastItem = lastItem.parent;
  }

  // Move tab sequence back to the menubar so we have a position to return to
  if (item.level !== 'menubar') {
    lastItem.activate(item);
  }
  menu.clear();
};

/**
 * Filter menu items for desktop or mobile.
 */
const filterMenuItems = menuItems => menuItems.filter(menuItem => {
  if (menuItem.data.backLink) {
    return !isDesktop();
  }
  if (menuItem.hasChildren) {
    menuItem.children = filterMenuItems(menuItem.children);
  }
  return true;
});

/**
 * Handle a key press for a menu item.
 *
 * See https://www.w3.org/TR/wai-aria-practices/#keyboard-interaction-12
 */
const handleKeyDown = (evt, menu) => {
  const {
    keyCode,
    target
  } = evt;
  const keys = {
    down: 40,
    end: 35,
    enter: 13,
    esc: 27,
    home: 36,
    left: 37,
    right: 39,
    tab: 9,
    space: 32,
    up: 38
  };
  const menuItems = filterMenuItems(menu.items);
  const currentItem = findMenuItem(menuItems, target);
  switch (keyCode) {
    case keys.enter:
    case keys.space:
      handleEnter(evt, currentItem);
      break;
    case keys.up:
      handleUpArrow(evt, currentItem);
      break;
    case keys.down:
      handleDownArrow(evt, currentItem);
      break;
    case keys.right:
      handleRightArrow(evt, currentItem);
      break;
    case keys.left:
      handleLeftArrow(evt, currentItem);
      break;
    case keys.home:
      handleHome(evt, currentItem);
      break;
    case keys.end:
      handleEnd(evt, currentItem);
      break;
    case keys.esc:
      handleEscape(evt, currentItem, menu);
      break;
    case keys.tab:
      handleTab(currentItem, menu);
      break;
    default:
  }
};

/**
 * Handle a menu item being clicked.
 */
const handleMenuClick = (evt, item, menu, back = false) => {
  if (!item || !isDesktop() && item.hasChildren) {
    // Prevent visiting links with children on mobile as we show the submenu instead
    evt.preventDefault();
  }
  if (item && back) {
    item.parent.activate(item);
  } else if (item && item.hasChildren && !back) {
    item.children[0].activate(item);
  } else if (!item) {
    menu.clear();
  }
};

/**
 * Get a menu item that incliudes the data required to build up an accessible menu.
 */
const getNewMenuItem = ({
  menu,
  item,
  level,
  index,
  parent,
  tabIndex,
  activeMenuKey,
  setActiveMenuKey
}) => {
  let domNode = null;
  const menuItem = {};

  // Callback ref to access the DOM node
  // https://reactjs.org/docs/refs-and-the-dom.html#callback-refs
  const ref = element => {
    domNode = element;
    menuItem.domNode = element;
  };
  Object.assign(menuItem, {
    data: item,
    ref,
    domNode,
    level,
    parent,
    children: [],
    hasChildren: hasChildren(item)
  });

  // Set a unique key
  menuItem.key = JSON.stringify([level, index, parent ? parent.data.label : '', item]);

  // Set properties to be used by the menu item link
  menuItem.linkProps = {
    ref,
    tabIndex,
    onClick: evt => handleMenuClick(evt, menuItem, menu, item.backLink),
    onKeyDown: evt => handleKeyDown(evt, menu)
  };

  // Assign props from the original menu item data
  Object.assign(menuItem.linkProps, item, {
    // Remove menu item children so as not to confuse with React children
    children: null,
    // Assumes any submenus are collapsed
    aria: menuItem.hasChildren ? {
      haspopup: true
    } : null
  });

  // Active a menu item, including setting the focus and tab index
  menuItem.activate = (previousItem, setActive = true) => {
    const currentDomNode = menuItem.domNode;
    if (setActive) {
      setActiveMenuKey(menuItem.key);
    }

    // Set roving tab index
    previousItem.domNode.setAttribute('tabindex', '-1');
    currentDomNode.setAttribute('tabindex', '0');
    currentDomNode.focus();

    // Use an implicit wait to give any menus with transitions a chance to
    // expand before focus, up to a maximum of 1 second
    let iterations = 0;
    const interval = setInterval(() => {
      currentDomNode.focus();
      if (document.activeElement || iterations > 50) {
        clearInterval(interval);
      }
      iterations += 1;
    }, 20);
  };

  // Check if the sub-menu containing children of this item is active
  menuItem.isActiveSubMenu = () => menuItem.children.findIndex(child => getDescendantKeys(child).indexOf(activeMenuKey) > -1) > -1;
  return menuItem;
};

/**
 * Get menu items with all of the data required to build accessible menus.
 */
const getAccessibleMenuItems = ({
  menu,
  menuItems,
  activeMenuKey,
  setActiveMenuKey,
  parent,
  level = 'menu'
}) => {
  const accessibleMenuItems = [];
  const commonAttrs = {
    menu,
    activeMenuKey,
    setActiveMenuKey
  };
  menuItems.forEach((item, index) => {
    const newItem = getNewMenuItem({
      item,
      parent,
      level,
      tabIndex: index === 0 && level === 'menubar' ? '0' : '-1',
      index,
      ...commonAttrs
    });
    accessibleMenuItems.push(newItem);
    if (hasChildren(item)) {
      newItem.children.push(...getAccessibleMenuItems({
        menuItems: item.children,
        parent: newItem,
        ...commonAttrs
      }));
    }
  });
  return accessibleMenuItems;
};

/**
 * Get the menu items with any mobile menu items appended.
 */
const getCombinedMenuItems = (menuItems, mobileMenuItems = []) => [...menuItems, ...mobileMenuItems.map(item => ({
  ...item,
  mobile: true
}))].filter(x => x);

/**
 * Add back links to the menu items.
 */
const extendMenuItemsWithBackLinks = (menuItems, backLink) => {
  menuItems.filter(hasChildren).filter(menuItem => !menuItem.children[0].backLink).forEach(menuItem => {
    menuItem.children.unshift({
      label: backLink,
      backLink: true
    });
    extendMenuItemsWithBackLinks(menuItem.children, menuItem.label);
  });
};

/**
 * Extend menu items with prev and next attributes.
 */
const extendMenuItemsWithPagination = menuItems => {
  const {
    length
  } = menuItems;
  menuItems.forEach(menuItem => {
    menuItem.prev = () => {
      const breakpointItems = menuItems.filter(item => !isDesktop() || !item.data.backLink);
      const i = breakpointItems.findIndex(item => item === menuItem);
      return i > 0 ? breakpointItems[i - 1] : null;
    };
    menuItem.next = () => {
      const breakpointItems = menuItems.filter(item => !isDesktop() || !item.data.backLink);
      const i = breakpointItems.findIndex(item => item === menuItem);
      return length - 1 > i ? breakpointItems[i + 1] : null;
    };
    if (menuItem.hasChildren) {
      extendMenuItemsWithPagination(menuItem.children);
    }
  });
};

/**
 * HOC to provide functionality for creating accessible menus.
 *
 * See https://www.w3.org/TR/wai-aria-practices/#menu
 */
const withAccessibleMenu = (WrappedComponent, options = {}) => props => {
  // eslint-disable-next-line react/prop-types
  const {
    menuItems,
    mobileMenuItems
  } = props;
  if (!menuItems) {
    throw new Error('A menuItems prop must be defined when using the withAccessibleMenu HOC');
  }
  const [activeMenuKey, setActiveMenuKey] = useState(null);
  const menu = {
    items: [],
    clear: () => setActiveMenuKey(null)
  };
  const combinedMenuItems = getCombinedMenuItems(menuItems, mobileMenuItems);
  if (options.withBackLinks) {
    extendMenuItemsWithBackLinks(combinedMenuItems, 'Main menu');
  }
  menu.items = getAccessibleMenuItems({
    menu,
    menuItems: combinedMenuItems,
    activeMenuKey,
    setActiveMenuKey,
    level: 'menubar'
  });
  extendMenuItemsWithPagination(menu.items);

  // Ensure the menu gets reset when switching between breakpoints
  useEffect(() => {
    const initialBreakpoint = window.innerWidth;
    const clear = () => {
      if (window.innerWidth >= desktopBreakpoint !== initialBreakpoint >= desktopBreakpoint) {
        menu.clear();
      }
    };
    const throttled = throttle(clear, 200);
    window.addEventListener('resize', throttled);
    return () => {
      window.removeEventListener('resize', throttled);
    };
  }, []);
  return /*#__PURE__*/React.createElement(WrappedComponent, _extends({}, props, {
    accessibleMenuItems: menu.items,
    onClose: menu.clear
  }));
};
export default withAccessibleMenu;